import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NAV_ITEMS, PRODUCTS } from "../constants";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";

export const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const navbarRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Change header to white when scrolled 70% of viewport height
          const scrollThreshold = window.innerHeight * 0.7;
          setScrolled(window.scrollY > scrollThreshold);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileBrandsOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Update navbar height when scrolled state changes
  useEffect(() => {
    const updateNavbarHeight = () => {
      if (navbarRef.current) {
        setNavbarHeight(navbarRef.current.offsetHeight);
      }
    };
    updateNavbarHeight();
    window.addEventListener("resize", updateNavbarHeight);
    return () => window.removeEventListener("resize", updateNavbarHeight);
  }, [scrolled]);

  const handleNavClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();

    // Map href to routes
    const routeMap: { [key: string]: string } = {
      "#about": "/about",
      "#products": "/products",
      "#blog": "/blog",
      "#salecenter": "/distributor",
      "#contact": "/contact",
    };

    const route = routeMap[href];

    if (route) {
      navigate(route);
      setMobileMenuOpen(false);
    } else if (href.startsWith("#")) {
      // If it's a hash link and we're on home page, scroll to section
      if (location.pathname === "/") {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to home and then scroll
        navigate("/");
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
      setMobileMenuOpen(false);
    }
  };

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav
      ref={navbarRef}
      className="font-raleway fixed top-0 left-0 right-0 z-50 py-4 shadow-sm bg-white transition-colors duration-300"
    >
      {/* Gradient border at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-gray-200 to-transparent"></div>

      {/* ${
      isHome 
        ? (scrolled ? 'bg-white/90 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-6')
        : 'bg-white/90 backdrop-blur-md py-4 shadow-sm'
    } */}
      <div className="container mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-1 select-none cursor-pointer"
        >
          <img
            src="/assets/logos/logo.webp"
            alt="Cromaply Logo"
            className="h-12 w-auto"
          />

        </Link>

        {/* Links */}
        <div
          className={`relative hidden lg:flex items-center py-3`}
        >
          {/* top */}
          <div className="absolute top-0 h-[1.5px] w-full bg-gradient-to-r from-[#e10016] to-transparent"></div>
          {/* left */}
          <div className="absolute left-0 h-full w-[1.5px] bg-gradient-to-b from-[#e10016] to-transparent"></div>
          {/* bottom */}
          <div className="absolute bottom-0 h-[1.5px] w-full bg-gradient-to-l from-[#005d9b] to-transparent"></div>
          {/* right */}
          <div className="absolute right-0 h-full w-[1.5px] bg-gradient-to-t from-[#005d9b] to-transparent"></div>
          {NAV_ITEMS.map((item) => {
            // Map nav items to routes
            const routeMap: { [key: string]: string } = {
              "About Us": "/about",
              "Our Brands": "/brands",
              Blog: "/blog",
              "Sale Center": "/distributor",
            };

            const route = routeMap[item.label];

            // Products: dropdown with all products (same pattern as Our Brands); click goes to /products
            if (item.label === "Products") {
              const isActive = location.pathname.startsWith('/products');
              const textClass = isActive ? "text-croma-red" : "text-gray-800";

              return (
                <div key={item.label} className="relative px-2 group">
                  <Link
                    to="/products"
                    className={`text-sm font-medium px-3 hover:opacity-70 transition-colors inline-flex items-center gap-1 ${textClass}`}
                    aria-haspopup="menu"
                    aria-label={item.label}
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4 opacity-80" />
                  </Link>

                  <div
                    className="absolute left-0 top-full mt-3 w-56 rounded-xl bg-white shadow-lg border border-gray-100 py-2 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0"
                    role="menu"
                  >
                    {PRODUCTS.map((product) => (
                      <Link
                        key={product.id}
                        to={`/products/${product.id}`}
                        className="block px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 hover:text-croma-red transition-colors"
                        role="menuitem"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            if (item.children?.length) {
              const isActive = item.label === "Our Brands" && location.pathname === "/brands";
              const textClass = isActive ? "text-croma-red" : "text-gray-800";

              return (
                <div key={item.label} className="relative px-2 group">
                  <button
                    type="button"
                    className={`text-sm font-medium px-3 hover:opacity-70 transition-colors inline-flex items-center gap-1 ${textClass}`}
                    aria-haspopup="menu"
                    aria-label={item.label}
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4 opacity-80" />
                  </button>

                  <div
                    className="absolute left-0 top-full mt-3 w-56 rounded-xl bg-white shadow-lg border border-gray-100 py-2 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0"
                    role="menu"
                  >
                    {item.children.map((child) => {
                      const labelContent = (
                        <>
                          {child.icon && (
                            <img
                              src={child.icon}
                              alt=""
                              className="w-5 h-5 object-contain shrink-0"
                              aria-hidden
                            />
                          )}
                          <span>{child.label}</span>
                        </>
                      );
                      const isPdf = /\.pdf$/i.test(child.href) || child.href.includes('/pdfs/');
                      const href = isPdf ? `/view-pdf?file=${encodeURIComponent(child.href)}` : child.href;
                      if (child.external || isPdf) {
                        return (
                          <a
                            key={child.label}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 hover:text-croma-red transition-colors"
                            role="menuitem"
                          >
                            {labelContent}
                          </a>
                        );
                      }

                      return (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 hover:text-croma-red transition-colors"
                          role="menuitem"
                        >
                          {labelContent}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            } else if (route && item.label !== "Products") {
              const isActive = location.pathname === route;
              return (
                <Link
                  key={item.label}
                  to={route}
                  className={`text-sm font-medium px-5 hover:opacity-70 transition-colors ${isActive ? 'text-croma-red' : 'text-gray-800'}`}
                >
                  {item.label}
                </Link>
              );
            } else if (item.label !== "Products") {
              return (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="text-sm font-medium px-5 hover:opacity-70 transition-colors text-gray-800"
                >
                  {item.label}
                </a>
              );
            }
            return null;
          })}
        </div>

        {/* CTA - Desktop Only */}
        <Link
          to="/contact"
          onClick={handleLinkClick}
          className={`hidden lg:flex px-6 py-2.5 rounded-lg text-sm font-medium items-center gap-2 group-hover:gap-3 group transition-all ${scrolled ? 'bg-croma-red text-white hover:bg-red-700' : 'bg-croma-red text-white hover:bg-red-700'}`}
        >
          Let's Discuss
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </Link>

        {/* Hamburger Menu Button - Mobile Only */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 rounded-lg transition-colors text-gray-800 hover:bg-gray-100"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        className={`lg:hidden fixed left-0 right-0 bg-white/90 backdrop-blur-md transition-all duration-300 ease-in-out ${mobileMenuOpen
          ? "opacity-100 translate-y-0 shadow-lg border-t border-gray-200"
          : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        style={{ zIndex: 100, top: `${navbarHeight}px` }}
      >
        <div className="container mx-auto px-6 py-6 space-y-4">
          {/* Mobile Nav Links */}
          {NAV_ITEMS.map((item) => {
            const routeMap: { [key: string]: string } = {
              "About Us": "/about",
              "Our Brands": "/brands",
              Blog: "/blog",
              "Sale Center": "/distributor",
            };

            const route = routeMap[item.label];

            // Mobile: Products dropdown with all products
            if (item.label === "Products") {
              return (
                <div key={item.label} className="py-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileProductsOpen((v) => !v);
                      setMobileBrandsOpen(false);
                    }}
                    className="w-full flex items-center justify-between text-base font-medium text-gray-800 hover:text-croma-red transition-colors"
                    aria-expanded={mobileProductsOpen}
                    aria-controls="mobile-products-menu"
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  <div
                    id="mobile-products-menu"
                    className={`mt-2 pl-3 border-l border-gray-200 space-y-1 ${mobileProductsOpen ? "block" : "hidden"
                      }`}
                  >
                    {PRODUCTS.map((product) => (
                      <Link
                        key={product.id}
                        to={`/products/${product.id}`}
                        onClick={handleLinkClick}
                        className="block text-base font-medium text-gray-700 hover:text-croma-red transition-colors py-1.5"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            if (item.children?.length) {
              return (
                <div key={item.label} className="py-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMobileBrandsOpen((v) => !v);
                      setMobileProductsOpen(false);
                    }}
                    className="w-full flex items-center justify-between text-base font-medium text-gray-800 hover:text-croma-red transition-colors"
                    aria-expanded={mobileBrandsOpen}
                    aria-controls="mobile-brands-menu"
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${mobileBrandsOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  <div
                    id="mobile-brands-menu"
                    className={`mt-2 pl-3 border-l border-gray-200 space-y-2 ${mobileBrandsOpen ? "block" : "hidden"
                      }`}
                  >
                    {item.children.map((child) => {
                      const labelContent = (
                        <>
                          {child.icon && (
                            <img
                              src={child.icon}
                              alt=""
                              className="w-5 h-5 object-contain shrink-0"
                              aria-hidden
                            />
                          )}
                          <span>{child.label}</span>
                        </>
                      );
                      const isPdf = /\.pdf$/i.test(child.href) || child.href.includes('/pdfs/');
                      const href = isPdf ? `/view-pdf?file=${encodeURIComponent(child.href)}` : child.href;
                      if (child.external || isPdf) {
                        return (
                          <a
                            key={child.label}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-2 text-base font-medium text-gray-700 hover:text-croma-red transition-colors py-1"
                          >
                            {labelContent}
                          </a>
                        );
                      }

                      return (
                        <Link
                          key={child.label}
                          to={child.href}
                          onClick={handleLinkClick}
                          className="flex items-center gap-2 text-base font-medium text-gray-700 hover:text-croma-red transition-colors py-1"
                        >
                          {labelContent}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            } else if (route && item.label !== "Products") {
              return (
                <Link
                  key={item.label}
                  to={route}
                  onClick={handleLinkClick}
                  className="block text-base font-medium text-gray-800 hover:text-croma-red transition-colors py-2"
                >
                  {item.label}
                </Link>
              );
            } else if (item.label !== "Products") {
              return (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="block text-base font-medium text-gray-800 hover:text-croma-red transition-colors py-2"
                >
                  {item.label}
                </a>
              );
            }
            return null;
          })}

          {/* Mobile CTA Button */}
          <Link
            to="/contact"
            onClick={handleLinkClick}
            className="w-full mt-6 px-6 py-3 rounded-lg text-sm font-medium !font-raleway bg-croma-red text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2 group"
          >
            Let's Discuss
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </nav>
  );
};
